import { authGuard, guestGuard } from './guards/auth'
import { permissionGuard, anyPermissionGuard } from './guards/permissions'
import { roleGuard, adminGuard, superAdminGuard } from './guards/roles'
import { sessionGuard, tokenRefreshGuard } from './guards/session'
import { Capacitor } from '@capacitor/core'

const routes = [
  // Rotas públicas
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: { requiresAuth: false },
    beforeEnter: (_to: any, _from: any, next: any) => {
      // Redirecionar para login de usuario em apps nativos
      if (Capacitor.isNativePlatform()) {
        next({ name: 'UsuarioLogin' })
        return
      }
      next()
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/auth/Register.vue'),
    meta: { guestOnly: true }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPassword.vue'),
    meta: { requiresAuth: false, allowOutsideUsuario: true }
  },
  {
    path: '/reset-password/:token',
    name: 'ResetPassword',
    component: () => import('@/views/auth/ResetPassword.vue'),
    meta: { requiresAuth: false, allowOutsideUsuario: true }
  },
  {
    path: '/veiculo/:id',
    name: 'PublicVeiculoHistorico',
    component: () => import('@/views/Public/VeiculoHistorico.vue'),
    meta: { requiresAuth: false, allowOutsideUsuario: true }
  },

  // Checkout de produtos do distribuidor (público)
  {
    path: '/distribuidor/produtos/:id/checkout',
    name: 'DistribuidorProdutoCheckout',
    component: () => import('@/views/Distribuidor/Produtos/Checkout.vue'),
    meta: { requiresAuth: false }
  },

  // Login específico para usuários (mobile-first)
  {
    path: '/usuario/login',
    name: 'UsuarioLogin',
    component: () => import('@/views/Usuario/Login.vue'),
    meta: { guestOnly: true }
  },

  // Registro específico para usuários (mobile-first)
  {
    path: '/usuario/register',
    name: 'UsuarioRegister',
    component: () => import('@/views/Usuario/Register.vue'),
    meta: { guestOnly: true }
  },

  // Dashboard principal (redireciona baseado no role)
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/Dashboard/Index.vue'),
    meta: { requiresAuth: true },
    beforeEnter: [authGuard, sessionGuard]
  },

  // Módulo Estoque
  {
    path: '/estoque',
    name: 'Estoque',
    component: () => import('@/views/Estoque/Index.vue'),
    meta: { requiresAuth: true, roles: ['oficina', 'estoquista', 'admin'] },
    beforeEnter: [authGuard, sessionGuard, roleGuard(['oficina', 'estoquista', 'admin'])]
  },

  // Módulo Oficina
  {
    path: '/oficina',
    name: 'Oficina',
    meta: { requiresAuth: true, roles: ['oficina', 'admin'] },
    beforeEnter: [authGuard, sessionGuard, roleGuard(['oficina', 'admin'])],
    children: [
      {
        path: '',
        name: 'OficinaDashboard',
        component: () => import('@/views/Oficina/Index.vue')
      },
      {
        path: 'perfil',
        name: 'OficinaPerfil',
        component: () => import('@/views/Oficina/Perfil/Index.vue')
      },
      {
        path: 'perfil/editar',
        name: 'OficinaPerfilEdit',
        component: () => import('@/views/Oficina/Perfil/Edit.vue')
      },
      {
        path: 'agendamentos',
        name: 'OficinaAgendamentos',
        component: () => import('@/views/Agendamentos/AgendamentosPage.vue')
      },
      {
        path: 'agendamentos/:id',
        name: 'OficinaAgendamentoShow',
        component: () => import('@/views/Oficina/Agendamentos/Show.vue')
      },
      {
        path: 'clientes',
        name: 'OficinaClientes',
        component: () => import('@/views/Oficina/Clientes/Index.vue')
      },
      {
        path: 'clientes/:id',
        name: 'OficinaClienteShow',
        component: () => import('@/views/Oficina/Clientes/Show.vue')
      },
      {
        path: 'clientes/criar',
        name: 'OficinaClienteCreate',
        component: () => import('@/views/Oficina/Clientes/Create.vue')
      },
      {
        path: 'clientes/:id/editar',
        name: 'OficinaClienteEdit',
        component: () => import('@/views/Oficina/Clientes/Edit.vue')
      },
      {
        path: 'veiculos',
        name: 'OficinaVeiculos',
        component: () => import('@/views/Oficina/Veiculos/Index.vue')
      },
      {
        path: 'veiculos/:id',
        name: 'OficinaVeiculoShow',
        component: () => import('@/views/Oficina/Veiculos/Show.vue')
      },
      {
        path: 'ordens-servico',
        name: 'OficinaOrdensServico',
        component: () => import('@/views/Oficina/OrdensServico/Index.vue')
      },
      {
        path: 'ordens-servico/:id',
        name: 'OficinaOrdemServicoShow',
        component: () => import('@/views/Oficina/OrdensServico/Show.vue')
      },
      {
        path: 'ordens-servico/criar',
        name: 'OficinaOrdemServicoCreate',
        component: () => import('@/views/Oficina/OrdensServico/Create.vue')
      },
      {
        path: 'orcamentos',
        name: 'OficinaOrcamentos',
        component: () => import('@/views/Oficina/Orcamentos/Index.vue')
      },
      {
        path: 'orcamentos/:id',
        name: 'OficinaOrcamentoShow',
        component: () => import('@/views/Oficina/Orcamentos/Show.vue')
      },
      {
        path: 'orcamentos/criar',
        name: 'OficinaOrcamentoCreate',
        component: () => import('@/views/Oficina/Orcamentos/Create.vue')
      },
      {
        path: 'checkin',
        name: 'OficinaCheckin',
        component: () => import('@/views/Oficina/Checkin/Index.vue')
      },
      {
        path: 'marketplace',
        name: 'OficinaMarketplace',
        component: () => import('@/views/Oficina/Marketplace/Index.vue')
      },
      {
        path: 'diagnostico',
        name: 'OficinaDiagnostico',
        component: () => import('@/views/Oficina/Diagnostico/Index.vue')
      },
      {
        path: 'diagnostico/criar',
        name: 'OficinaDiagnosticoCreate',
        component: () => import('@/views/Oficina/Diagnostico/Create.vue')
      },
      {
        path: 'diagnostico/:id',
        name: 'OficinaDiagnosticoShow',
        component: () => import('@/views/Oficina/Diagnostico/Show.vue')
      }
    ]
  },

  // Módulo Mecânico
  {
    path: '/mecanico',
    name: 'Mecanico',
    component: () => import('@/layouts/MecanicoLayout.vue'),
    meta: { requiresAuth: true, roles: ['mecanico', 'admin'] },
    children: [
      {
        path: '',
        name: 'MecanicoDashboard',
        component: () => import('@/views/Mecanico/Index.vue')
      },
      {
        path: 'agendamentos',
        name: 'MecanicoAgendamentos',
        component: () => import('@/views/Mecanico/Agendamentos/Index.vue')
      },
      {
        path: 'agendamentos/:id',
        name: 'MecanicoAgendamentoShow',
        component: () => import('@/views/Mecanico/Agendamentos/Show.vue')
      },
      {
        path: 'ordens',
        name: 'MecanicoOrdensServico',
        component: () => import('@/views/Mecanico/OrdensServico/Index.vue')
      },
      {
        path: 'checklist',
        name: 'MecanicoChecklist',
        component: () => import('@/views/Mecanico/Checklist/Index.vue')
      }
    ]
  },

  // Módulo Distribuidor
  {
    path: '/distribuidor',
    name: 'Distribuidor',
    meta: { requiresAuth: true, roles: ['distribuidor', 'admin'] },
    children: [
      {
        path: '',
        name: 'DistribuidorDashboard',
        component: () => import('@/views/Distribuidor/Index.vue')
      },
      {
        path: 'produtos',
        name: 'DistribuidorProdutos',
        component: () => import('@/views/Distribuidor/Produtos/Index.vue')
      },
      {
        path: 'produtos/criar',
        name: 'DistribuidorProdutoCreate',
        component: () => import('@/views/Distribuidor/Produtos/Create.vue')
      },
      {
        path: 'produtos/:id/editar',
        name: 'DistribuidorProdutoEdit',
        component: () => import('@/views/Distribuidor/Produtos/Edit.vue')
      },
      {
        path: 'pedidos',
        name: 'DistribuidorPedidos',
        component: () => import('@/views/Distribuidor/Pedidos/Index.vue')
      },
      {
        path: 'pedidos/:id',
        name: 'DistribuidorPedidoShow',
        component: () => import('@/views/Distribuidor/Pedidos/Show.vue')
      },
      {
        path: 'marketplace',
        name: 'DistribuidorMarketplace',
        component: () => import('@/views/Distribuidor/Marketplace/Index.vue')
      }
    ]
  },

  // Módulo Admin
  {
    path: '/admin',
    name: 'Admin',
    meta: { requiresAuth: true, roles: ['admin'] },
    beforeEnter: [authGuard, sessionGuard, roleGuard(['admin'])],
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        component: () => import('@/views/Admin/Dashboard.vue')
      },
      {
        path: 'usuarios',
        name: 'AdminUsuarios',
        component: () => import('@/views/Admin/Usuarios/Index.vue')
      },
      {
        path: 'oficinas',
        name: 'AdminOficinas',
        component: () => import('@/views/Admin/Oficinas/Index.vue')
      },
      {
        path: 'distribuidores',
        name: 'AdminDistribuidores',
        component: () => import('@/views/Admin/Distribuidores/Index.vue')
      },
      {
        path: 'relatorios',
        name: 'AdminRelatorios',
        component: () => import('@/views/Admin/Relatorios/Index.vue')
      },
      {
        path: 'configuracoes',
        name: 'AdminConfiguracoes',
        component: () => import('@/views/Admin/Configuracoes/Index.vue')
      }
    ]
  },

  // Módulo Frota
  {
    path: '/frota',
    name: 'Frota',
    meta: { requiresAuth: true, roles: ['frota_gestor', 'admin'] },
    beforeEnter: [authGuard, sessionGuard, roleGuard(['frota_gestor', 'admin'])],
    children: [
      {
        path: '',
        name: 'FrotaDashboard',
        component: () => import('@/views/Frota/Dashboard.vue')
      },
      {
        path: 'perfil',
        name: 'FrotaPerfil',
        component: () => import('@/views/Frota/Perfil/Index.vue')
      },
      {
        path: 'perfil/editar',
        name: 'FrotaPerfilEdit',
        component: () => import('@/views/Frota/Perfil/Edit.vue')
      },
      {
        path: 'perfil/senha',
        name: 'FrotaPerfilSenha',
        component: () => import('@/views/Frota/Perfil/Password.vue')
      }
    ]
  },
  // Módulo Financeiro - COMENTADO TEMPORARIAMENTE
  // {
  //   path: '/financeiro',
  //   name: 'Financeiro',
  //   meta: { requiresAuth: true },
  //   children: [
  // {
  //   path: '',
  //   name: 'FinanceiroDashboard',
  //   component: () => import('@/views/Financeiro/Index.vue')
  // },
  // {
  //   path: 'contas-pagar',
  //   name: 'FinanceiroContasPagar',
  //   component: () => import('@/views/Financeiro/ContasPagar/Index.vue')
  // },
  // {
  //   path: 'contas-receber',
  //   name: 'FinanceiroContasReceber',
  //   component: () => import('@/views/Financeiro/ContasReceber/Index.vue')
  // },
  // {
  //   path: 'pdv',
  //   name: 'FinanceiroPdv',
  //   component: () => import('@/views/Financeiro/Pdv/Index.vue')
  //       // }
  // ]
  // },

  // Módulo Produtos
  {
    path: '/produtos',
    name: 'Produtos',
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'ProdutosIndex',
        component: () => import('@/views/Produtos/Index.vue')
      },
      {
        path: 'criar',
        name: 'ProdutoCreate',
        component: () => import('@/views/Produtos/Create.vue')
      },
      {
        path: ':id/editar',
        name: 'ProdutoEdit',
        component: () => import('@/views/Produtos/Edit.vue')
      }
    ]
  },

  // Módulo Usuário (Mobile)
  {
    path: '/usuario',
    name: 'Usuario',
    meta: { requiresAuth: true, roles: ['usuario'] },
    beforeEnter: [authGuard, sessionGuard, roleGuard(['usuario'])],
    redirect: { name: 'UsuarioHome' },
    children: [
      {
        path: '',
        name: 'UsuarioHome',
        component: () => import('@/views/Usuario/Home.vue')
      },
      {
        path: 'home-simple',
        name: 'UsuarioHomeSimple',
        component: () => import('@/views/Usuario/HomeSimple.vue')
      },
      {
        path: 'buscar',
        name: 'UsuarioBuscar',
        component: () => import('@/views/Usuario/Buscar.vue')
      },
      {
        path: 'perfil',
        name: 'UsuarioPerfil',
        component: () => import('@/views/Usuario/Perfil/Index.vue')
      },
      {
        path: 'perfil/editar',
        name: 'UsuarioPerfilEdit',
        component: () => import('@/views/Usuario/Perfil/Edit.vue')
      },
      {
        path: 'perfil/senha',
        name: 'UsuarioPerfilSenha',
        component: () => import('@/views/Usuario/Perfil/Password.vue')
      },
      {
        path: 'perfil/notificacoes',
        name: 'UsuarioPerfilNotificacoes',
        component: () => import('@/views/Usuario/Perfil/Notifications.vue')
      },
      {
        path: 'perfil/privacidade',
        name: 'UsuarioPerfilPrivacidade',
        component: () => import('@/views/Usuario/Perfil/Privacy.vue')
      },
      {
        path: 'veiculos',
        name: 'UsuarioVeiculoIndex',
        component: () => import('@/views/Usuario/Veiculos/Index.vue')
      },
      {
        path: 'veiculos/adicionar',
        name: 'UsuarioVeiculoCreate',
        component: () => import('@/views/Usuario/Veiculos/Create.vue')
      },
      {
        path: 'veiculos/:id',
        name: 'UsuarioVeiculoShow',
        component: () => import('@/views/Usuario/Veiculos/Show.vue')
      },
      {
        path: 'veiculos/:id/editar',
        name: 'UsuarioVeiculoEdit',
        component: () => import('@/views/Usuario/Veiculos/Edit.vue')
      },
      {
        path: 'oficina/:id',
        name: 'UsuarioOficinaShow',
        component: () => import('@/views/Usuario/Oficina/Show.vue')
      },
      {
        path: 'favoritos',
        name: 'UsuarioFavoritos',
        component: () => import('@/views/Usuario/Favoritos.vue')
      },
      {
        path: 'emergencia',
        name: 'UsuarioEmergencia',
        component: () => import('@/views/Usuario/Emergencia/Index.vue')
      },
      {
        path: 'emergencia/:id/finalizado',
        name: 'UsuarioEmergenciaFinalizado',
        component: () => import('@/views/Usuario/Emergencia/Finalizado.vue')
      },
      {
        path: 'emergencia/:id/pagamento/selecionar',
        name: 'UsuarioEmergenciaSelecionarPagamento',
        component: () => import('@/views/Usuario/Emergencia/SelecionarPagamento.vue')
      },
      {
        path: 'emergencia/:id/pagamento/recebido',
        name: 'UsuarioEmergenciaPagamentoRecebido',
        component: () => import('@/views/Usuario/Emergencia/PagamentoRecebido.vue')
      },
      {
        path: 'agendamentos',
        name: 'UsuarioAgendamentos',
        component: () => import('@/views/Usuario/Agendamentos/Index.vue')
      },
      {
        path: 'agendamentos/:id',
        name: 'UsuarioAgendamentoShow',
        component: () => import('@/views/Usuario/Agendamentos/Show.vue')
      },
      {
        path: 'agendamentos/:id/pagamento',
        name: 'UsuarioAgendamentoPagamento',
        component: () => import('@/views/Usuario/Agendamentos/SelecionarPagamento.vue')
      },
      {
        path: 'agendar/:oficinaId',
        name: 'UsuarioAgendar',
        component: () => import('@/views/Usuario/Agendar/Create.vue')
      },
      {
        path: 'diagnosticos',
        name: 'UsuarioDiagnosticos',
        component: () => import('@/views/Usuario/Diagnosticos/Index.vue')
      },
      {
        path: 'diagnostico/:id',
        name: 'UsuarioDiagnosticoShow',
        component: () => import('@/views/Usuario/Diagnosticos/Show.vue')
      },
      {
        path: 'agendar-diagnostico/:oficinaId',
        name: 'UsuarioAgendarDiagnostico',
        component: () => import('@/views/Usuario/Diagnosticos/AgendarDiagnostico.vue')
      },
      {
        path: 'orcamentos',
        name: 'UsuarioOrcamentos',
        component: () => import('@/views/Usuario/Orcamentos/Index.vue')
      },
      {
        path: 'orcamento/:id',
        name: 'UsuarioOrcamentoShow',
        component: () => import('@/views/Usuario/Orcamentos/Show.vue')
      },
      {
        path: 'orcamento/:id/pagar',
        name: 'UsuarioOrcamentoPagar',
        component: () => import('@/views/Usuario/Orcamentos/Pagar.vue')
      },
      {
        path: 'orcamento/:id/agendar-servico',
        name: 'UsuarioOrcamentoAgendarServico',
        component: () => import('@/views/Usuario/Orcamentos/AgendarServico.vue')
      },
      {
        path: 'cotacao/:diagnosticoId/comparar',
        name: 'UsuarioCotacaoComparar',
        component: () => import('@/views/Usuario/Cotacao/Comparar.vue')
      },
      {
        path: 'historico',
        name: 'UsuarioHistorico',
        component: () => import('@/views/Usuario/Historico/Index.vue')
      },
      {
        path: 'historico/veiculo/:veiculoId',
        name: 'UsuarioHistoricoVeiculo',
        component: () => import('@/views/Usuario/Historico/Veiculo.vue')
      },
      {
        path: 'historico/manutencoes',
        name: 'UsuarioHistoricoManutencoes',
        component: () => import('@/views/Usuario/Historico/Manutencoes.vue')
      },
      {
        path: 'ipva',
        name: 'UsuarioIpva',
        component: () => import('@/views/Usuario/Ipva/Index.vue')
      },
      {
        path: 'ipva/consultar-valor',
        name: 'UsuarioIpvaConsultar',
        component: () => import('@/views/Usuario/Ipva/Consultar.vue')
      },
      {
        path: 'ipva/contratar',
        name: 'UsuarioIpvaContratar',
        component: () => import('@/views/Usuario/Ipva/Contratar.vue')
      },
      {
        path: 'pagamentos',
        name: 'UsuarioPagamentos',
        component: () => import('@/views/Usuario/Pagamentos/Index.vue')
      },
      {
        path: 'pagamentos/criar',
        name: 'UsuarioPagamentoCreate',
        component: () => import('@/views/Usuario/Pagamentos/Create.vue')
      },
      {
        path: 'pagamentos/pix',
        name: 'UsuarioPagamentoPix',
        component: () => import('@/views/Usuario/Pagamentos/Pix.vue')
      },
      {
        path: 'pagamentos/pix/:id/status',
        name: 'UsuarioPagamentoPixStatus',
        component: () => import('@/views/Usuario/Pagamentos/PixStatus.vue')
      },
      {
        path: 'pagamentos/historico',
        name: 'UsuarioPagamentoHistorico',
        component: () => import('@/views/Usuario/Pagamentos/Historico.vue')
      },
      {
        path: 'pagamentos/comprovante/:id',
        name: 'UsuarioPagamentoComprovante',
        component: () => import('@/views/Usuario/Pagamentos/Comprovante.vue')
      },
      // {
      //   path: 'avaliacoes',
      //   name: 'UsuarioAvaliacoes',
      //   component: () => import('@/views/Usuario/Avaliacoes/Index.vue')
      // },
      // {
      //   path: 'avaliacoes/oficina/:oficinaId',
      //   name: 'UsuarioAvaliacaoOficina',
      //   component: () => import('@/views/Usuario/Avaliacoes/Oficina.vue')
      // },
      // {
      //   path: 'avaliacoes/oficina/:oficinaId/criar',
      //   name: 'UsuarioAvaliacaoCreate',
      //   component: () => import('@/views/Usuario/Avaliacoes/Create.vue')
      // },
      // {
      //   path: 'avaliacoes/minhas',
      //   name: 'UsuarioAvaliacoesMinhas',
      //   component: () => import('@/views/Usuario/Avaliacoes/Minhas.vue')
      // },
      // {
      //   path: 'avaliacoes/:id',
      //   name: 'UsuarioAvaliacaoShow',
      //   component: () => import('@/views/Usuario/Avaliacoes/Show.vue')
      // },
      // {
      //   path: 'suporte/ajuda',
      //   name: 'UsuarioSuporteAjuda',
      //   component: () => import('@/views/Usuario/Suporte/Ajuda.vue')
      // },
      // {
      //   path: 'suporte/contato',
      //   name: 'UsuarioSuporteContato',
      //   component: () => import('@/views/Usuario/Suporte/Contato.vue')
      // },
      // {
      //   path: 'suporte/termos',
      //   name: 'UsuarioSuporteTermos',
      //   component: () => import('@/views/Usuario/Suporte/Termos.vue')
      // },
      // {
      //   path: 'emergencia',
      //   name: 'UsuarioEmergencia',
      //   component: () => import('@/views/Usuario/Emergencia/Index.vue')
      // },
      // {
      //   path: 'emergencia/:id/status',
      //   name: 'UsuarioEmergenciaStatus',
      //   component: () => import('@/views/Usuario/Emergencia/Status.vue')
      // },
      // {
      //   path: 'qr-code/veiculo/:veiculoId/gerar',
      //   name: 'UsuarioQrCodeGerar',
      //   component: () => import('@/views/Usuario/QrCode/Gerar.vue')
      // },
      // {
      //   path: 'qr-code/veiculo/:veiculoId/visualizar',
      //   name: 'UsuarioQrCodeVisualizar',
      //   component: () => import('@/views/Usuario/QrCode/Visualizar.vue')
      // },
      // {
      //   path: 'qr-code/veiculo/:veiculoId/historico',
      //   name: 'UsuarioQrCodeHistorico',
      //   component: () => import('@/views/Usuario/QrCode/Historico.vue')
      // },
      // {
      //   path: 'qr-code/veiculo/:veiculoId/configuracoes',
      //   name: 'UsuarioQrCodeConfiguracoes',
      //   component: () => import('@/views/Usuario/QrCode/Configuracoes.vue')
      // },
      // {
      //   path: 'mobile/qr-code/dashboard',
      //   name: 'UsuarioMobileQrCodeDashboard',
      //   component: () => import('@/views/Usuario/Mobile/QrCode/Dashboard.vue')
      // },
      // {
      //   path: 'mobile/qr-code/veiculo/:veiculoId/gerar',
      //   name: 'UsuarioMobileQrCodeGerar',
      //   component: () => import('@/views/Usuario/Mobile/QrCode/Gerar.vue')
      // },
      // {
      //   path: 'mobile/qr-code/veiculo/:veiculoId/visualizar',
      //   name: 'UsuarioMobileQrCodeVisualizar',
      //   component: () => import('@/views/Usuario/Mobile/QrCode/Visualizar.vue')
      // },
      // {
      //   path: 'mobile/qr-code/veiculo/:veiculoId/compartilhar',
      //   name: 'UsuarioMobileQrCodeCompartilhar',
      //   component: () => import('@/views/Usuario/Mobile/QrCode/Compartilhar.vue')
      // },
      // {
      //   path: 'mobile/qr-code/veiculo/:veiculoId/historico',
      //   name: 'UsuarioMobileQrCodeHistorico',
      //   component: () => import('@/views/Usuario/Mobile/QrCode/Historico.vue')
      // }
    ]
  },

  // Módulo Compras - COMENTADO TEMPORARIAMENTE
  // {
  //   path: '/compras',
  //   name: 'Compras',
  //   meta: { requiresAuth: true, roles: ['admin', 'compras_gestor', 'compras_aprovador'] },
  //   beforeEnter: [authGuard, sessionGuard, roleGuard(['admin', 'compras_gestor', 'compras_aprovador'])],
  //   children: [
  // {
  //   path: '',
  //   name: 'ComprasDashboard',
  //   component: () => import('@/views/Compras/Dashboard/Index.vue')
  // },
  // // {
  // //   path: 'ordens-compra',
  // //   name: 'ComprasOrdensCompra',
  // //   component: () => import('@/views/Compras/OrdensCompra/Index.vue')
  // // },
  // {
  //   path: 'ordens-compra/criar',
  //   name: 'ComprasOrdemCompraCreate',
  //   component: () => import('@/views/Compras/OrdensCompra/Create.vue')
  // },
  // {
  //   path: 'ordens-compra/:id',
  //   name: 'ComprasOrdemCompraShow',
  //   component: () => import('@/views/Compras/OrdensCompra/Show.vue')
  // },
  // {
  //   path: 'ordens-compra/:id/editar',
  //   name: 'ComprasOrdemCompraEdit',
  //   component: () => import('@/views/Compras/OrdensCompra/Edit.vue')
  // },
  // {
  //   path: 'ordens-compra/:id/aprovar',
  //   name: 'ComprasOrdemCompraAprovar',
  //   component: () => import('@/views/Compras/OrdensCompra/Aprovar.vue')
  // },
  // {
  //   path: 'ordens-compra/:id/rejeitar',
  //   name: 'ComprasOrdemCompraRejeitar',
  //   component: () => import('@/views/Compras/OrdensCompra/Rejeitar.vue')
  // },
  // {
  //   path: 'ordens-compra/:id/enviar',
  //   name: 'ComprasOrdemCompraEnviar',
  //   component: () => import('@/views/Compras/OrdensCompra/Enviar.vue')
  // },
  // {
  //   path: 'sugestoes-reposicao',
  //   name: 'ComprasSugestoesReposicao',
  //   component: () => import('@/views/Compras/SugestoesReposicao/Index.vue')
  // },
  // {
  //   path: 'sugestoes-reposicao/criar',
  //   name: 'ComprasSugestaoReposicaoCreate',
  //   component: () => import('@/views/Compras/SugestoesReposicao/Create.vue')
  // },
  // {
  //   path: 'sugestoes-reposicao/:id',
  //   name: 'ComprasSugestaoReposicaoShow',
  //   component: () => import('@/views/Compras/SugestoesReposicao/Show.vue')
  // },
  // {
  //   path: 'sugestoes-reposicao/:id/processar',
  //   name: 'ComprasSugestaoReposicaoProcessar',
  //   component: () => import('@/views/Compras/SugestoesReposicao/Processar.vue')
  // },
  // {
  //   path: 'sugestoes-reposicao/:id/criar-ordem',
  //   name: 'ComprasSugestaoReposicaoCriarOrdem',
  //   component: () => import('@/views/Compras/SugestoesReposicao/CriarOrdem.vue')
  // },
  // {
  //   path: 'recebimentos',
  //   name: 'ComprasRecebimentos',
  //   component: () => import('@/views/Compras/Recebimentos/Index.vue')
  // },
  // {
  //   path: 'recebimentos/criar',
  //   name: 'ComprasRecebimentoCreate',
  //   component: () => import('@/views/Compras/Recebimentos/Create.vue')
  // },
  // {
  //   path: 'recebimentos/:id',
  //   name: 'ComprasRecebimentoShow',
  //   component: () => import('@/views/Compras/Recebimentos/Show.vue')
  // },
  // {
  //   path: 'recebimentos/:id/processar',
  //   name: 'ComprasRecebimentoProcessar',
  //   component: () => import('@/views/Compras/Recebimentos/Processar.vue')
  // },
  // {
  //   path: 'recebimentos/:id/cancelar',
  //   name: 'ComprasRecebimentoCancelar',
  //   component: () => import('@/views/Compras/Recebimentos/Cancelar.vue')
  // },
  // {
  //   path: 'relatorios',
  //   name: 'ComprasRelatorios',
  //   component: () => import('@/views/Compras/Relatorios/Index.vue')
  // },
  // {
  //   path: 'relatorios/ordens-por-periodo',
  //   name: 'ComprasRelatorioOrdensPorPeriodo',
  //   component: () => import('@/views/Compras/Relatorios/OrdensPorPeriodo.vue')
  // },
  // {
  //   path: 'relatorios/gastos-por-distribuidor',
  //   name: 'ComprasRelatorioGastosPorDistribuidor',
  //   component: () => import('@/views/Compras/Relatorios/GastosPorDistribuidor.vue')
  // },
  // {
  //   path: 'relatorios/produtos-mais-comprados',
  //   name: 'ComprasRelatorioProdutosMaisComprados',
  //   component: () => import('@/views/Compras/Relatorios/ProdutosMaisComprados.vue')
  // },
  // {
  //   path: 'relatorios/exportar-excel',
  //   name: 'ComprasRelatorioExportarExcel',
  //   component: () => import('@/views/Compras/Relatorios/ExportarExcel.vue')
  // },
  // // {
  // //   path: 'relatorios/exportar-pdf',
  // //   name: 'ComprasRelatorioExportarPdf',
  // //   component: () => import('@/views/Compras/Relatorios/ExportarPdf.vue')
  // // }
  // ]
  // },

  // Módulo Serviços
  {
    path: '/servicos',
    name: 'Servicos',
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'ServicosIndex',
        component: () => import('@/views/Servicos/Index.vue')
      }
    ]
  },

  // Módulo Pagamentos (APIs Específicas) - COMENTADO TEMPORARIAMENTE
  // {
  //   path: '/pagamentos',
  //   name: 'Pagamentos',
  //   meta: { requiresAuth: true },
  //   children: [
  // {
  //   path: '',
  //   name: 'PagamentosDashboard',
  //   component: () => import('@/views/Pagamentos/Dashboard/Index.vue')
  // },
  // {
  //   path: 'pix',
  //   name: 'PagamentosPix',
  //   component: () => import('@/views/Pagamentos/Pix/Index.vue')
  // },
  // {
  //   path: 'pix/criar',
  //   name: 'PagamentosPixCreate',
  //   component: () => import('@/views/Pagamentos/Pix/Create.vue')
  // },
  // {
  //   path: 'pix/:id/status',
  //   name: 'PagamentosPixStatus',
  //   component: () => import('@/views/Pagamentos/Pix/Status.vue')
  // },
  // {
  //   path: 'pix/:id/qr-code',
  //   name: 'PagamentosPixQrCode',
  //   component: () => import('@/views/Pagamentos/Pix/QrCode.vue')
  // },
  // {
  //   path: 'pix/:id/cancelar',
  //   name: 'PagamentosPixCancelar',
  //   component: () => import('@/views/Pagamentos/Pix/Cancelar.vue')
  // },
  // {
  //   path: 'pix/:id/confirmar',
  //   name: 'PagamentosPixConfirmar',
  //   component: () => import('@/views/Pagamentos/Pix/Confirmar.vue')
  // },
  // {
  //   path: 'pix/:id/reembolsar',
  //   name: 'PagamentosPixReembolsar',
  //   component: () => import('@/views/Pagamentos/Pix/Reembolsar.vue')
  // },
  // {
  //   path: 'cartao',
  //   name: 'PagamentosCartao',
  //   component: () => import('@/views/Pagamentos/Cartao/Index.vue')
  // },
  // {
  //   path: 'cartao/processar',
  //   name: 'PagamentosCartaoProcessar',
  //   component: () => import('@/views/Pagamentos/Cartao/Processar.vue')
  // },
  // {
  //   path: 'cartao/:id/confirmar',
  //   name: 'PagamentosCartaoConfirmar',
  //   component: () => import('@/views/Pagamentos/Cartao/Confirmar.vue')
  // },
  // {
  //   path: 'cartao/:id/cancelar',
  //   name: 'PagamentosCartaoCancelar',
  //   component: () => import('@/views/Pagamentos/Cartao/Cancelar.vue')
  // },
  // {
  //   path: 'parcelamento',
  //   name: 'PagamentosParcelamento',
  //   component: () => import('@/views/Pagamentos/Parcelamento/Index.vue')
  // },
  // {
  //   path: 'parcelamento/calcular',
  //   name: 'PagamentosParcelamentoCalcular',
  //   component: () => import('@/views/Pagamentos/Parcelamento/Calcular.vue')
  // },
  // {
  //   path: 'parcelamento/validar',
  //   name: 'PagamentosParcelamentoValidar',
  //   component: () => import('@/views/Pagamentos/Parcelamento/Validar.vue')
  // },
  // {
  //   path: 'parcelamento/estatisticas',
  //   name: 'PagamentosParcelamentoEstatisticas',
  //   component: () => import('@/views/Pagamentos/Parcelamento/Estatisticas.vue')
  // },
  // {
  //   path: 'parcelamento/pagamentos/:pagamentoId',
  //   name: 'PagamentosParcelamentoLista',
  //   component: () => import('@/views/Pagamentos/Parcelamento/Lista.vue')
  // },
  // {
  //   path: 'parcelamento/pagamentos/:pagamentoId/criar',
  //   name: 'PagamentosParcelamentoCriar',
  //   component: () => import('@/views/Pagamentos/Parcelamento/Criar.vue')
  // }
  // ]
  // },

  // Módulo Transações - COMENTADO TEMPORARIAMENTE
  // {
  //   path: '/transacoes',
  //   name: 'Transacoes',
  //   meta: { requiresAuth: true },
  //   children: [
  // {
  //   path: '',
  //   name: 'TransacoesIndex',
  //   component: () => import('@/views/Transacoes/Index.vue')
  // },
  // {
  //   path: 'dashboard',
  //   name: 'TransacoesDashboard',
  //   component: () => import('@/views/Transacoes/Dashboard.vue')
  // },
  // {
  //   path: 'recentes',
  //   name: 'TransacoesRecentes',
  //   component: () => import('@/views/Transacoes/Recentes.vue')
  // },
  // {
  //   path: 'estatisticas',
  //   name: 'TransacoesEstatisticas',
  //   component: () => import('@/views/Transacoes/Estatisticas.vue')
  // },
  // {
  //   path: 'relatorio',
  //   name: 'TransacoesRelatorio',
  //   component: () => import('@/views/Transacoes/Relatorio.vue')
  // },
  // {
  //   path: 'performance-gateway',
  //   name: 'TransacoesPerformanceGateway',
  //   component: () => import('@/views/Transacoes/PerformanceGateway.vue')
  // },
  // {
  //   path: 'relatorio-reembolsos',
  //   name: 'TransacoesRelatorioReembolsos',
  //   component: () => import('@/views/Transacoes/RelatorioReembolsos.vue')
  // },
  // {
  //   path: 'relatorio-receita',
  //   name: 'TransacoesRelatorioReceita',
  //   component: () => import('@/views/Transacoes/RelatorioReceita.vue')
  // },
  // {
  //   path: 'exportar-csv',
  //   name: 'TransacoesExportarCsv',
  //   component: () => import('@/views/Transacoes/ExportarCsv.vue')
  // },
  // {
  //   path: 'pagamento/:pagamentoId',
  //   name: 'TransacoesPorPagamento',
  //   component: () => import('@/views/Transacoes/PorPagamento.vue')
  // },
  // {
  //   path: ':id',
  //   name: 'TransacaoShow',
  //   component: () => import('@/views/Transacoes/Show.vue')
  // },
  // {
  //   path: ':id/reembolsar',
  //   name: 'TransacaoReembolsar',
  //   component: () => import('@/views/Transacoes/Reembolsar.vue')
  // },
  // {
  //   path: ':id/cancelar',
  //   name: 'TransacaoCancelar',
  //   component: () => import('@/views/Transacoes/Cancelar.vue')
  // },
  // {
  //   path: ':id/atualizar-status',
  //   name: 'TransacaoAtualizarStatus',
  //   component: () => import('@/views/Transacoes/AtualizarStatus.vue')
  // }
  // ]
  // },

  // Módulo Monitoramento - COMENTADO TEMPORARIAMENTE
  // {
  //   path: '/monitoramento',
  //   name: 'Monitoramento',
  //   meta: { requiresAuth: true, roles: ['admin', 'devops'] },
  //   beforeEnter: [authGuard, sessionGuard, roleGuard(['admin', 'devops'])],
  //   children: [
  // {
  //   path: '',
  //   name: 'MonitoramentoDashboard',
  //   component: () => import('@/views/Monitoramento/Dashboard.vue')
  // },
  // {
  //   path: 'health',
  //   name: 'MonitoramentoHealth',
  //   component: () => import('@/views/Monitoramento/Health.vue')
  // },
  // {
  //   path: 'metrics',
  //   name: 'MonitoramentoMetrics',
  //   component: () => import('@/views/Monitoramento/Metrics.vue')
  // }
  // ]
  // },

  // Módulo Recebimentos - COMENTADO TEMPORARIAMENTE
  // {
  //   path: '/recebimentos',
  //   name: 'Recebimentos',
  //   meta: { requiresAuth: true },
  //   children: [
  // // {
  // //   path: '',
  // //   name: 'RecebimentosIndex',
  // //   component: () => import('@/views/Recebimentos/Index.vue')
  // // },
  // // {
  // //   path: 'criar',
  // //   name: 'RecebimentoCreate',
  // //   component: () => import('@/views/Recebimentos/Create.vue')
  // // },
  // // {
  // //   path: ':id',
  // //   name: 'RecebimentoShow',
  // //   component: () => import('@/views/Recebimentos/Show.vue')
  // // },
  // // {
  // //   path: ':id/processar',
  // //   name: 'RecebimentoProcessar',
  // //   component: () => import('@/views/Recebimentos/Processar.vue')
  // // },
  // // {
  // //   path: ':id/cancelar',
  // //   name: 'RecebimentoCancelar',
  // //   component: () => import('@/views/Recebimentos/Cancelar.vue')
  // // },
  // // {
  // //   path: 'relatorio',
  // //   name: 'RecebimentosRelatorio',
  // //   component: () => import('@/views/Recebimentos/Relatorio.vue')
  // // },
  // {
  //   path: 'ordens-disponiveis',
  //   name: 'RecebimentosOrdensDisponiveis',
  //   component: () => import('@/views/Recebimentos/OrdensDisponiveis.vue')
  // },
  // {
  //   path: 'criar/:ordemCompraId',
  //   name: 'RecebimentoCriar',
  //   component: () => import('@/views/Recebimentos/Criar.vue')
  // }
  // ]
  // },

  // Módulo Rastreamento - COMENTADO TEMPORARIAMENTE
  // {
  //   path: '/rastreamento',
  //   name: 'Rastreamento',
  //   meta: { requiresAuth: true },
  //   children: [
  // {
  //   path: 'ordens-compra/:ordemCompraId/historico',
  //   name: 'RastreamentoHistorico',
  //   component: () => import('@/views/Rastreamento/Historico.vue')
  // },
  // {
  //   path: 'ordens-compra/:ordemCompraId/status-atual',
  //   name: 'RastreamentoStatusAtual',
  //   component: () => import('@/views/Rastreamento/StatusAtual.vue')
  // },
  // {
  //   path: 'ordens-compra/:ordemCompraId/atualizar-status',
  //   name: 'RastreamentoAtualizarStatus',
  //   component: () => import('@/views/Rastreamento/AtualizarStatus.vue')
  // },
  // {
  //   path: 'ordens-por-status',
  //   name: 'RastreamentoOrdensPorStatus',
  //   component: () => import('@/views/Rastreamento/OrdensPorStatus.vue')
  // },
  // {
  //   path: 'estatisticas',
  //   name: 'RastreamentoEstatisticas',
  //   component: () => import('@/views/Rastreamento/Estatisticas.vue')
  // },
  // {
  //   path: 'sincronizar',
  //   name: 'RastreamentoSincronizar',
  //   component: () => import('@/views/Rastreamento/Sincronizar.vue')
  // },
  // {
  //   path: 'dashboard',
  //   name: 'RastreamentoDashboard',
  //   component: () => import('@/views/Rastreamento/Dashboard.vue')
  // }
  // ]
  // },

  // Módulo Notificações
  {
    path: '/notificacoes',
    name: 'Notificacoes',
    component: () => import('@/views/Notificacoes/Index.vue'),
    meta: { requiresAuth: true }
  },

  // Módulo Configurações
  {
    path: '/configuracoes',
    name: 'Configuracoes',
    component: () => import('@/views/Configuracoes/Index.vue'),
    meta: { requiresAuth: true }
  },

  // Perfil do usuário
  {
    path: '/perfil',
    name: 'Profile',
    component: () => import('@/views/profile/UserProfile.vue'),
    meta: { requiresAuth: true }
  },

  // Rotas de veículos (mantidas para compatibilidade)
  {
    path: '/vehicles',
    name: 'Vehicles',
    component: () => import('@/views/vehicles/VehicleList.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/vehicles/:id',
    name: 'VehicleDetail',
    component: () => import('@/views/vehicles/VehicleDetail.vue'),
    meta: { requiresAuth: true }
  },

  // Manutenção (mantida para compatibilidade)
  {
    path: '/maintenance',
    name: 'Maintenance',
    component: () => import('@/views/maintenance/MaintenanceList.vue'),
    meta: { requiresAuth: true }
  },

  // Rota de erro 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/errors/NotFound.vue')
  }
]

export default routes
